import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import type { User } from "firebase/auth";

import pkg from "../package.json";

const apiKey = import.meta.env.VITE_BUGSNAG_API_KEY;

// No-op when the notifier key isn't configured (tests, key-less local
// dev). The plan's "lazy-import via dynamic `await import('@bugsnag/js')`"
// trade-off doesn't hold once `BugsnagBoundary` is mounted at the root
// layout — the React plugin's ErrorBoundary class has to be created
// synchronously at component-definition time, so Bugsnag is on the
// critical path of every page regardless. Synchronous import keeps the
// init flow simple and the type story honest.
export function startBugsnag(): void {
  if (!apiKey || Bugsnag.isStarted()) return;
  Bugsnag.start({
    apiKey,
    plugins: [new BugsnagPluginReact()],
    releaseStage: process.env.NODE_ENV,
    enabledReleaseStages: ["production"],
    appVersion: pkg.version,
  });
}

export function notifyBugsnag(error: unknown): void {
  // Auto-start so server-side callers (API routes, error.tsx files) don't
  // need a separate init step. Idempotent via `isStarted()`, no-op when
  // the key is missing.
  if (!Bugsnag.isStarted()) startBugsnag();
  if (!Bugsnag.isStarted()) return;
  const err = error instanceof Error ? error : new Error(String(error));
  Bugsnag.notify(err);
}

export function setBugsnagUser(user: User | null): void {
  if (!Bugsnag.isStarted()) return;
  if (user) {
    Bugsnag.setUser(
      user.uid,
      user.email ?? undefined,
      user.displayName ?? undefined,
    );
  } else {
    Bugsnag.setUser(undefined, undefined, undefined);
  }
}

export { Bugsnag };
